"use client";

import { useId } from "react";
import { Area, AreaChart, ResponsiveContainer, YAxis } from "recharts";

// The little trend line under a Home stat card: a smooth area that fades
// from the accent colour to transparent, no axes, grid, labels or tooltip.
// Purely a shape — the number above it is the readout.
export function Sparkline({
  values,
  color,
  scale = "zero",
  className = "h-[34px]",
}: {
  values: number[];
  color: string;
  // "zero": the baseline is 0, so the height means "how much" (listeners,
  // bandwidth). "fit": zoom to the data's own range so a slow change shows
  // up (storage only ever creeps upward from a big base).
  scale?: "zero" | "fit";
  className?: string;
}) {
  // A gradient id has to be unique per chart or every sparkline on the page
  // ends up using the first one's colour.
  const gradientId = useId().replace(/:/g, "");
  const data = values.map((v, i) => ({ i, v }));
  const lo = values.length ? Math.min(...values) : 0;
  const hi = values.length ? Math.max(...values) : 0;
  const domain: [number, number] =
    scale === "fit" ? (hi === lo ? [lo - 1, hi + 1] : [lo, hi]) : [0, Math.max(hi, 1)];

  return (
    <div className={`mt-2.5 w-full ${className}`} aria-hidden="true">
      {data.length < 2 ? (
        // Not enough history yet: a faint flat line so the card keeps its height.
        <div className="mt-4 h-px w-full opacity-40" style={{ backgroundColor: color }} />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            {/* Hidden axis: only here to set the scale, nothing is drawn. */}
            <YAxis hide domain={domain} />
            <Area
              type="monotone"
              dataKey="v"
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              isAnimationActive={false}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
